/**
 * Reads content from the clipboard and returns text or files.
 *
 * Attempts to read clipboard content using the modern Clipboard API with a fallback
 * to the deprecated execCommand method for older browsers. When multiple types exist,
 * text/plain is preferred over files.
 *
 * @returns {Promise<Object>} Resolves to an object with clipboard content
 * @returns {Promise<{type: 'text', data: string}>} When clipboard contains text
 * @returns {Promise<{type: 'files', data: Blob[]}>} When clipboard contains only files
 *
 * @throws {Error} When clipboard cannot be read or content is inaccessible
 *
 * @example
 * const result = await pasteFromClipboard();
 * if (result.type === 'text') {
 *   const actions = JSON.parse(result.data);
 * } else if (result.type === 'files') {
 *   const files = result.data; // Array of Blob objects
 * }
 *
 * @browser Modern Clipboard API (Chrome 66+, Firefox 63+, Safari 13.1+)
 * @browser Fallback: execCommand method (all browsers, text only)
 */
export default async function pasteFromClipboard() {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        const items = await navigator.clipboard.read();

        for (const item of items) {
          if (item.types.includes("text/plain")) {
            const blob = await item.getType("text/plain");
            const text = await blob.text();
            return { type: "text", data: text };
          }
        }

        const files = [];
        for (const item of items) {
          for (const type of item.types) {
            if (!type.startsWith("text/")) {
              const blob = await item.getType(type);
              files.push(blob);
            }
          }
        }

        if (files.length > 0) {
          return { type: "files", data: files };
        }

        return { type: "text", data: "" };
      } catch (_readError) {
        try {
          const text = await navigator.clipboard.readText();
          return { type: "text", data: text };
        } catch (_textError) {
          throw new Error("Failed to read from clipboard", { cause: _textError });
        }
      }
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = "";
      textarea.style.position = "fixed";
      textarea.style.left = "-999999px";
      textarea.style.top = "-999999px";
      textarea.tabIndex = -1;
      textarea.classList.add("visually-hidden");
      document.body.appendChild(textarea);
      try {
        textarea.focus();
        document.execCommand("paste");
        const text = textarea.value;
        return { type: "text", data: text };
      } finally {
        textarea.remove();
      }
    }
  } catch (_error) {
    throw new Error("Failed to read from clipboard", { cause: _error });
  }
}
